/**
 * Available Privileges in the TapLearnGo System
 * 
 * Use these constants with the Hidden component to control UI visibility
 * based on user privileges.
 * 
 * Example usage:
 * import { PRIVILEGES } from '../constants/privileges';
 * 
 * <Hidden privilege={PRIVILEGES.SYSTEM_ADMIN}>
 *   <Button>System Admin Only</Button>
 * </Hidden>
 */

export const PRIVILEGES = {
  // Authentication & User Management
  VIEW_USERS: 'VIEW_USERS',
  CREATE_USER: 'CREATE_USER',
  UPDATE_USER: 'UPDATE_USER',
  DELETE_USER: 'DELETE_USER',
  MANAGE_USER_ROLES: 'MANAGE_USER_ROLES',
  VIEW_USER_PROFILE: 'VIEW_USER_PROFILE',
  UPDATE_USER_PROFILE: 'UPDATE_USER_PROFILE',
  
  // Group Management
  VIEW_GROUPS: 'VIEW_GROUPS',
  CREATE_GROUP: 'CREATE_GROUP',
  UPDATE_GROUP: 'UPDATE_GROUP',
  DELETE_GROUP: 'DELETE_GROUP',
  ASSIGN_GROUP_PRIVILEGES: 'ASSIGN_GROUP_PRIVILEGES',
  MANAGE_GROUP_MEMBERS: 'MANAGE_GROUP_MEMBERS',
  
  // Business Unit Management
  VIEW_BUSINESS_UNITS: 'VIEW_BUSINESS_UNITS',
  CREATE_BUSINESS_UNIT: 'CREATE_BUSINESS_UNIT',
  UPDATE_BUSINESS_UNIT: 'UPDATE_BUSINESS_UNIT',
  DELETE_BUSINESS_UNIT: 'DELETE_BUSINESS_UNIT',
  MANAGE_DEPARTMENTS: 'MANAGE_DEPARTMENTS',
  VIEW_BUSINESS_UNIT_REPORTS: 'VIEW_BUSINESS_UNIT_REPORTS',
  
  // Participant Management
  VIEW_PARTICIPANTS: 'VIEW_PARTICIPANTS',
  CREATE_PARTICIPANT: 'CREATE_PARTICIPANT',
  UPDATE_PARTICIPANT: 'UPDATE_PARTICIPANT',
  DELETE_PARTICIPANT: 'DELETE_PARTICIPANT',
  MANAGE_PARTICIPANT_ASSIGNMENTS: 'MANAGE_PARTICIPANT_ASSIGNMENTS',
  SEND_INVITATIONS: 'SEND_INVITATIONS',
  
  // Settings Management
  VIEW_SETTINGS: 'VIEW_SETTINGS',
  MANAGE_REGIONS: 'MANAGE_REGIONS',
  MANAGE_MANAGING_GROUPS: 'MANAGE_MANAGING_GROUPS',
  MANAGE_INDUSTRIES: 'MANAGE_INDUSTRIES',
  MANAGE_COUNTRIES: 'MANAGE_COUNTRIES',
  UPDATE_ORGANIZATION_INFO: 'UPDATE_ORGANIZATION_INFO',
  
  // Audit Trail
  VIEW_AUDIT_TRAIL: 'VIEW_AUDIT_TRAIL',
  EXPORT_AUDIT_TRAIL: 'EXPORT_AUDIT_TRAIL',
  MANAGE_AUDIT_SETTINGS: 'MANAGE_AUDIT_SETTINGS',
  
  // System Administration
  SYSTEM_ADMIN: 'SYSTEM_ADMIN',
  MANAGE_SYSTEM_SETTINGS: 'MANAGE_SYSTEM_SETTINGS',
  VIEW_SYSTEM_LOGS: 'VIEW_SYSTEM_LOGS',
  BACKUP_RESTORE: 'BACKUP_RESTORE',
  
  // Content Management
  VIEW_CONTENT: 'VIEW_CONTENT',
  CREATE_CONTENT: 'CREATE_CONTENT',
  UPDATE_CONTENT: 'UPDATE_CONTENT',
  DELETE_CONTENT: 'DELETE_CONTENT',
  PUBLISH_CONTENT: 'PUBLISH_CONTENT',
  
  // Questionnaire Management
  VIEW_QUESTIONNAIRE: 'VIEW_QUESTIONNAIRE',
  CREATE_QUESTIONNAIRE: 'CREATE_QUESTIONNAIRE',
  UPDATE_QUESTIONNAIRE: 'UPDATE_QUESTIONNAIRE',
  DELETE_QUESTIONNAIRE: 'DELETE_QUESTIONNAIRE',
  PUBLISH_QUESTIONNAIRE: 'PUBLISH_QUESTIONNAIRE',
  
  // Reports & Analytics
  VIEW_REPORTS: 'VIEW_REPORTS',
  CREATE_REPORTS: 'CREATE_REPORTS',
  EXPORT_REPORTS: 'EXPORT_REPORTS',
  VIEW_ANALYTICS: 'VIEW_ANALYTICS',
  
  // API Access
  API_ACCESS: 'API_ACCESS',
  EXTERNAL_INTEGRATIONS: 'EXTERNAL_INTEGRATIONS'
};

// Privilege categories for better organization
export const PRIVILEGE_CATEGORIES = {
  USER_MANAGEMENT: [
    PRIVILEGES.VIEW_USERS,
    PRIVILEGES.CREATE_USER,
    PRIVILEGES.UPDATE_USER,
    PRIVILEGES.DELETE_USER,
    PRIVILEGES.MANAGE_USER_ROLES,
    PRIVILEGES.VIEW_USER_PROFILE,
    PRIVILEGES.UPDATE_USER_PROFILE
  ],
  
  GROUP_MANAGEMENT: [
    PRIVILEGES.VIEW_GROUPS,
    PRIVILEGES.CREATE_GROUP,
    PRIVILEGES.UPDATE_GROUP,
    PRIVILEGES.DELETE_GROUP,
    PRIVILEGES.ASSIGN_GROUP_PRIVILEGES,
    PRIVILEGES.MANAGE_GROUP_MEMBERS
  ],
  
  BUSINESS_UNIT_MANAGEMENT: [
    PRIVILEGES.VIEW_BUSINESS_UNITS,
    PRIVILEGES.CREATE_BUSINESS_UNIT,
    PRIVILEGES.UPDATE_BUSINESS_UNIT,
    PRIVILEGES.DELETE_BUSINESS_UNIT,
    PRIVILEGES.MANAGE_DEPARTMENTS,
    PRIVILEGES.VIEW_BUSINESS_UNIT_REPORTS
  ],
  
  PARTICIPANT_MANAGEMENT: [
    PRIVILEGES.VIEW_PARTICIPANTS,
    PRIVILEGES.CREATE_PARTICIPANT,
    PRIVILEGES.UPDATE_PARTICIPANT,
    PRIVILEGES.DELETE_PARTICIPANT,
    PRIVILEGES.MANAGE_PARTICIPANT_ASSIGNMENTS,
    PRIVILEGES.SEND_INVITATIONS
  ],
  
  SYSTEM_ADMINISTRATION: [
    PRIVILEGES.SYSTEM_ADMIN,
    PRIVILEGES.MANAGE_SYSTEM_SETTINGS,
    PRIVILEGES.VIEW_SYSTEM_LOGS,
    PRIVILEGES.BACKUP_RESTORE
  ]
};